<template>
  <div class="date-time-selector">
    <!-- Date navigation -->
    <div class="date-navigation">
      <button class="btn-prev-week btn-back" @click="showPreviousWeek" :disabled="isPreviousWeekDisabled">
        <img :src="arrowIconBlack" alt="Icona">
      </button>

      <div class="date-range legend16-bold">
        {{ formattedDateRange }}
      </div>

      <button class="btn-next-week" @click="showNextWeek" :disabled="isNextWeekDisabled">
        <img :src="arrowIconBlack" alt="Icona">
      </button>
    </div>

    <!-- Calendar days -->
    <div class="calendar-days">
      <div v-for="day in visibleDays" :key="day.date" class="calendar-day" :class="{
        available: day.available,
        unavailable: !day.available,
        selected: isDateSelected(day.date),
      }" @click="day.available && selectDate(day.date)">
        <div class="day-number legend16-bold">{{ getDayNumber(day.date) }}</div>
        <div class="day-name legend16-light">{{ getDayName(day.date) }}</div>
      </div>
    </div>

    <!-- Time slots -->
    <div class="time-slots" v-if="selectedDateModel">
      <h3 class="time-slots-title legend16-bold">{{ $t('datetime.select_time_slot') }}</h3>

      <div class="time-slots-grid">
        <div v-for="timeSlot in availableTimesForSelectedDate" :key="timeSlot.value" class="time-slot legend16-bold" :class="{
          selected: selectedTimeModel === timeSlot.value,
          'current-appointment': timeSlot.isCurrentAppointment,
        }" @click="selectTime(timeSlot)">
          {{ timeSlot.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import arrowIconBlack from '@/assets/images/arrow-black-icon.svg';

export default {
  name: "DateTimeSelector",

  props: {
    availableDates: {
      type: Array,
      required: true,
    },
    selectedDate: {
      type: String,
      default: null,
    },
    selectedTime: {
      type: String,
      default: null,
    },
    currentAppointment: {
      type: Object,
      default: null,
    },
  },

  data () {
    return {
      currentWeekStart: this.calculateCurrentWeekStart(),
      selectedDateModel: this.selectedDate,
      selectedTimeModel: this.selectedTime,
      arrowIconBlack
    };
  },

  computed: {
    visibleDays () {
      const days = [];
      const currentDate = new Date(this.currentWeekStart);

      // Generate 7 days starting from current week start
      for (let i = 0; i < 7; i++) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() + i);

        const dateString = this.formatDate(date);
        const available = this.isDateAvailable(dateString);

        days.push({
          date: dateString,
          available,
        });
      }

      return days;
    },

    formattedDateRange () {
      const startDate = new Date(this.currentWeekStart);
      const endDate = new Date(this.currentWeekStart);
      endDate.setDate(endDate.getDate() + 6);

      // Determina la lingua corrente per il formatter
      const locale = this.getCurrentLocale();

      const startFormatted = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(startDate);

      const endFormatted = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(endDate);

      return `${startFormatted.split(" ")[0]}-${endFormatted}`;
    },

    isPreviousWeekDisabled () {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const weekStart = new Date(this.currentWeekStart);
      return weekStart <= today;
    },

    isNextWeekDisabled () {
      // Disable if there are no available dates in next week
      const nextWeekStart = new Date(this.currentWeekStart);
      nextWeekStart.setDate(nextWeekStart.getDate() + 7);

      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);

      // Check if there are any available dates in the next week
      return !this.availableDates.some((dateObj) => {
        const date = new Date(dateObj.date);
        return date >= nextWeekStart && date <= nextWeekEnd;
      });
    },

    availableTimesForSelectedDate () {
      if (!this.selectedDateModel) {
        return [];
      }

      // Trova l'oggetto data selezionata nell'array availableDates
      const selectedDateObj = this.availableDates.find(
        (dateObj) => dateObj.date === this.selectedDateModel
      );

      // Se non c'è corrispondenza o non ci sono fasce orarie, ritorna array vuoto
      if (!selectedDateObj || !selectedDateObj.available_slots) {
        return [];
      }

      // Trasforma gli slot disponibili per la visualizzazione
      return selectedDateObj.available_slots.map((slot) => ({
        value: `${slot.startTime}-${slot.endTime}`, // Valore identificativo
        // label: `${this.formatTime(slot.startTime)} - ${this.formatTime(slot.endTime)}`,
        label: `${this.formatTime(slot.startTime)}`,
        id: slot.id, // Manteniamo anche l'id per riferimento
        isCurrentAppointment: slot.isCurrentAppointment || false, // Nuovo campo
      }));
    },
  },

  watch: {
    selectedDate (newVal) {
      this.selectedDateModel = newVal;
    },

    selectedTime (newVal) {
      this.selectedTimeModel = newVal;
    },

    availableDates: {
      handler (newDates) {
        // Adjust current week start if no dates are available in current week
        if (this.visibleDays.every((day) => !day.available)) {
          this.findNextAvailableWeek();
        }

        // Se abbiamo date disponibili e un appuntamento, proviamo a inizializzare
        if (newDates && newDates.length > 0) {
          this.initializeFromCurrentAppointment();
        }
      },
      immediate: true,
    },

    currentAppointment: {
      handler: "initializeFromCurrentAppointment",
      immediate: true,
    },
  },

  methods: {
    // Determina la lingua corrente per la formattazione delle date
    getCurrentLocale () {
      // Determina la lingua basandoti sulle impostazioni di WordPress/WPML
      if (typeof window.rolexAppointmentSettings !== 'undefined') {
        if (window.rolexAppointmentSettings.wpmlLanguage) {
          const lang = window.rolexAppointmentSettings.wpmlLanguage;
          // Converte il codice lingua al formato Intl.DateTimeFormat (es. 'it' -> 'it-IT')
          return lang === 'en' ? 'en-US' : `${lang}-${lang.toUpperCase()}`;
        }
      }

      // Fallback a italiano
      return 'it-IT';
    },

    initializeFromCurrentAppointment () {
      if (
        this.currentAppointment &&
        this.availableDates &&
        this.availableDates.length > 0
      ) {
        // Seleziona automaticamente la data dall'appuntamento
        if (this.currentAppointment.date) {
          this.selectedDateModel = this.currentAppointment.date;

          // Aggiorna la settimana corrente per mostrare la data selezionata
          this.updateCurrentWeekForDate(this.currentAppointment.date);

          // Emetti l'evento di selezione data
          this.$emit("select-date", this.currentAppointment.date);

          // Dopo aver selezionato la data, aspetta un momento per permettere agli
          // slot orari di essere caricati e poi seleziona l'ora
          this.$nextTick(() => {
            if (this.currentAppointment.timeSlot) {
              this.selectTime(this.currentAppointment.timeSlot);
            }
          });
        }
      }
    },

    updateCurrentWeekForDate (dateString) {
      const date = new Date(dateString);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
      this.currentWeekStart = new Date(date.setDate(diff));
    },

    calculateCurrentWeekStart () {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // If selectedDate is provided, use that week
      if (this.selectedDate) {
        const selectedDate = new Date(this.selectedDate);
        const day = selectedDate.getDay();
        const diff = selectedDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
        return new Date(selectedDate.setDate(diff));
      }

      // Otherwise start from current week
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
      return new Date(today.setDate(diff));
    },

    formatDate (date) {
      return date.toISOString().split("T")[0];
    },

    formatTime (timeString) {
      // Convert 24h time to display format
      const [hours, minutes] = timeString.split(":");
      return `${hours}:${minutes}`;
    },

    isDateAvailable (dateString) {
      return this.availableDates.some((dateObj) => dateObj.date === dateString);
    },

    getDayNumber (dateString) {
      return new Date(dateString).getDate();
    },

    getDayName (dateString) {
      const date = new Date(dateString);
      const dayIndex = date.getDay();

      // Usa le traduzioni per i nomi dei giorni
      return this.$t(`datetime.days_short.${dayIndex}`);
    },

    isDateSelected (dateString) {
      return this.selectedDateModel === dateString;
    },

    selectDate (dateString) {
      this.selectedDateModel = dateString;
      this.selectedTimeModel = null; // Reset time when date changes
      this.$emit("select-date", dateString);
    },

    selectTime (timeSlot) {
      this.selectedTimeModel = timeSlot.value;

      this.$emit("select-time", {
        startTime: timeSlot.startTime,
        endTime: timeSlot.endTime,
        value: timeSlot.value,
        id: timeSlot.id,
        isCurrentAppointment: timeSlot.isCurrentAppointment,
      });
    },

    showPreviousWeek () {
      if (this.isPreviousWeekDisabled) return;

      const prevWeek = new Date(this.currentWeekStart);
      prevWeek.setDate(prevWeek.getDate() - 7);
      this.currentWeekStart = prevWeek;
    },

    showNextWeek () {
      if (this.isNextWeekDisabled) return;

      const nextWeek = new Date(this.currentWeekStart);
      nextWeek.setDate(nextWeek.getDate() + 7);
      this.currentWeekStart = nextWeek;
    },

    findNextAvailableWeek () {
      if (this.availableDates.length === 0) return;

      // Find the earliest available date
      const earliestDate = this.availableDates.reduce((earliest, current) => {
        return new Date(current.date) < new Date(earliest.date) ? current : earliest;
      });

      // Set the week to include this date
      const date = new Date(earliestDate.date);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
      this.currentWeekStart = new Date(date.setDate(diff));
    },
  },

  created () {
    this.initializeFromCurrentAppointment();
  },
};
</script>
<style scoped>
/* Aggiungi questi stili per evidenziare lo slot dell'appuntamento corrente */
.time-slot.current-appointment {
  background-color: #f0f0f0;
  border: 1px dashed #aaa;
  color: #777;
}

.time-slot.current-appointment.selected {
  background-color: #e0e0e0;
  border: 1px solid #999;
  color: #555;
}
</style>